import { useCallback, useState } from 'react';
import {
  createProduct,
  deleteProduct,
  getAllProducts,
  updateProduct
} from '../firebase/products';

/**
 * Product management hook backed by Firestore.
 * @param {Array} initialProducts - Initial products array
 * @returns {Object} Product management functions and state
 */
export function useProductManagement(initialProducts = []) {
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch products for admin view
   */
  const fetchProducts = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    const result = await getAllProducts(options);
    if (result.error) {
      setError(result.error);
    } else {
      setProducts(result.products);
    }

    setLoading(false);
    return result;
  }, []);

  /**
   * Create product in Firestore and update local state
   */
  const createProductEntry = useCallback(async (productData) => {
    setLoading(true);
    setError(null);

    const result = await createProduct(productData);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchProducts();
    }

    setLoading(false);
    return result;
  }, [fetchProducts]);

  /**
   * Update product in Firestore and update local state
   */
  const updateProductEntry = useCallback(async (productId, updates) => {
    setLoading(true);
    setError(null);

    const result = await updateProduct(productId, updates);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchProducts();
    }

    setLoading(false);
    return result;
  }, [fetchProducts]);

  /**
   * Delete product in Firestore and update local state
   */
  const deleteProductEntry = useCallback(async (productId) => {
    setLoading(true);
    setError(null);

    const result = await deleteProduct(productId);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchProducts();
    }

    setLoading(false);
    return result;
  }, [fetchProducts]);

  return {
    products,
    setProducts,
    loading,
    error,
    fetchProducts,
    createProductEntry,
    updateProductEntry,
    deleteProductEntry
  };
}
